import { h, Fragment } from 'preact'
import { useRef, useErrorBoundary, useMemo } from 'preact/hooks'
import PNFO from 'jlinc-shared/PNFO'

import classNames from 'lib/classNames'
import sortBy from 'lib/sortBy'
import { useAppState } from 'lib/appState'
import { useNotifications } from 'lib/notificationsHooks'
import { useOrganization, usePublicProfile, useMyPublicProfile } from 'lib/membershipAppStateHooks'
import useOnScrolledIntoView from 'lib/useOnScrolledIntoViewHook'
import { publicProfileToDisplayName, publicProfileToPathname } from 'lib/publicProfiles'

import Link from 'components/Link'
import TimeAgo from 'components/TimeAgo'
import _OrganizationIcon from 'components/OrganizationIcon'
import Icon from 'components/Icon'
import InfiniteScrollDown from 'components/InfiniteScrollDown'
import ErrorMessage from 'components/ErrorMessage'
import './index.sass'
import { useChatSynopsis, useChatMessages } from 'lib/chatHooks'


export default function NotificationsList({ onNotificationClick }) {
  const {
    notifications,
    loadingNotifications,
    loadingNotificationsError,
    lastPageOfNotifications,
    loadNotifications,
  } = useNotifications('NotificationsList')
  const { myPublicProfile } = useMyPublicProfile('NotificationsList')

  return <InfiniteScrollDown
    className="NotificationsList"
    name="notifications"
    loadMore={loadNotifications}
    loading={loadingNotifications}
    loadingError={loadingNotificationsError}
    fullyLoaded={lastPageOfNotifications}
    loaderSize="md"
    emptyMessage={`You have no notifications yet`}
    fullyLoadedMessage={`You have no more notifications`}
  >
    {sortBy(notifications, [n => -(new Date(n.createdAt)).getTime()])
      .map(notification =>
        <NotificationWithErrorCatching {...{
          key: notification.uid,
          notification,
          onNotificationClick,
          myPublicProfile,
        }}/>
      )
    }
  </InfiniteScrollDown>
}

const TYPES = {
  nodeMembershipInviteAccepted: ({ organizationApikey, inviteePublicProfileDid }) => ({
    icon: <OrganizationIcon {...{organizationApikey}}/>,
    href: `/${organizationApikey}/members`,
    message: <span>
      <PublicProfileName did={inviteePublicProfileDid}/>
      &nbsp;has accepted your invite to join&nbsp;
      <OrganizationName {...{organizationApikey}}/>
    </span>,
  }),
  nodeMembershipRequestResolved: ({ organizationApikey, accept}) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}`,
    message: <span>
      <OrganizationName {...{organizationApikey}}/> has {accept ? 'accepted' : 'declined'} your request to join
    </span>,
  }),
  nodeMembershipRoleChange: ({ organizationApikey, granted, role }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}/members`,
    message: <span>
      You are {granted ? 'now a' : 'no longer a'}&nbsp;
      {role} of <OrganizationName {...{organizationApikey}}/>
    </span>,
  }),
  requestedNodeMembership: ({ organizationApikey, requesterPublicProfileDid }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}/members`,
    message: <span>
      <PublicProfileName did={requesterPublicProfileDid}/>
      &nbsp;requested to join&nbsp;
      <OrganizationName {...{organizationApikey}}/>
    </span>,
  }),
  sisaPermissionChanged: ({ organizationApikey, sisaEventId }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}/sisa-events/${sisaEventId}`,
    message: <span>
      You updated permissions under your SISA with&nbsp;
      <OrganizationName {...{organizationApikey}}/>
    </span>,
  }),
  sisaPersonalDataChanged: ({ organizationApikey, sisaEventId }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}/sisa-events/${sisaEventId}`,
    message: <span>
      You updated your personal data under your SISA with&nbsp;
      <OrganizationName {...{organizationApikey}}/>
    </span>,
  }),
  inviteToJoinOrganization: ({ organizationApikey }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}/join`,
    message: <span>
      <OrganizationName {...{organizationApikey}}/>
      &nbsp;has invited you to join their {PNFO.singular}
    </span>,
  }),
  inviteToJoinOrganizationNetwork: ({ organizationApikey, networkOrganizationApikey }) => ({
    icon: <OrganizationIcon {...{organizationApikey}}/>,
    href: `/${organizationApikey}/admin/networks/invites`,
    message: <span>
      <OrganizationName {...{organizationApikey}}/>
      &nbsp;has been invited to be listed on&nbsp;
      <OrganizationName organizationApikey={networkOrganizationApikey}/>'s Network
    </span>,
  }),
  inviteToJoinOrganizationNetworkResolved: ({ organizationApikey, memberOrganizationApikey, accepted }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: (accepted
      ? `/${organizationApikey}/network/${memberOrganizationApikey}`
      : `/${organizationApikey}/admin/network/invite?q=` +
      encodeURIComponent(memberOrganizationApikey)
    ),
    message: <span>
      <OrganizationName organizationApikey={memberOrganizationApikey}/>
      &nbsp;has {accepted ? 'accepted' : 'declined'} your invitation to be listed on&nbsp;
      <OrganizationName {...{organizationApikey}}/>'s Network
    </span>,
  }),
  removedFromOrganizationNetwork: ({ organizationApikey, networkOrganizationApikey }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${networkOrganizationApikey}/network`,
    message: <span>
      <OrganizationName {...{organizationApikey}}/>
      &nbsp;has been unlisted from&nbsp;
      <OrganizationName organizationApikey={networkOrganizationApikey}/>'s Network
    </span>,
  }),
  promptToCompletePublicProfile: ({ myPublicProfile: publicProfile }) => ({
    icon: <Icon type="edit"/>,
    href: `${publicProfileToPathname({ ...publicProfile })}/edit`,
    message: <span>
      Complete your profile. Upload an avatar and choose a display name.
    </span>,
  }),
  promptToManageNotificationSettings: () => ({
    icon: <Icon type="notifications"/>,
    href: '/settings',
    message: <span>
      Visit settings to turn on desktop notifications + manage email & sms/mobile notifications.
    </span>,
  }),
  organizationAnnouncement: ({ organizationApikey }) => ({

    icon: <Icon type="megaphone" />,
    href: `/${organizationApikey}/forum`,
    message: <span>
      <OrganizationName {...{organizationApikey}}/> has a new announcement
    </span>,

  }),

  newCommentOnPost: ({ organizationApikey, feedPostUid, commentCount }) => ({
    icon: <Icon type="singlecomment" />,
    href: `/${organizationApikey}/forum/${feedPostUid}`,
    message: <span>
      {commentCount > 1 ? (
        <span>{commentCount} new comments on your post</span>
      ) : (
        <span>New comment on your post</span>
      )}
    </span>,
  }),

  mutedUserNotification: ({ organizationapikey: organizationApikey, comments, mute_type, reason, expires_at }) => {

    const isIndefinite = expires_at === ''
    const expirationMessage = isIndefinite ? 'indefinitely' : expires_at ?
      `until ${new Date(expires_at).toLocaleDateString()}` : ''

    let actionMessage = ''
    switch (mute_type) {
    case 'hubchat':
      actionMessage = `paused from chatting in `
      break
    case 'post':
      actionMessage = `paused from posting in `
      break
    case 'comment':
      actionMessage = `paused from commenting in `
      break
    case 'DM':
      actionMessage = `paused from private chats in `
      break
    default:
      actionMessage = `paused in `
    }

    return {
      icon: <Icon type="mute" />,
      href: '',
      message: (
        <span>
          You have been   {actionMessage}  <OrganizationName {...{ organizationApikey }} /> {expirationMessage}
          {reason ? `, Reason: ${reason}` : ''}, <br />
          <strong> {comments ? `comments: ${comments}` : ''}</strong>
          <br />
          <strong>NOTE:</strong> Appeal to Admins
        </span>
      ),
    }
  },

  unmutedUserNotification: ({ organizationapikey: organizationApikey }) => ({

    icon: <Icon type="mute" />,
    href: '',
    message: <span>
      You have been unmuted from  <OrganizationName {...{ organizationApikey }} />
    </span>

  }),

  chatMessageDeletedByAdminandCurator: ({channelUid, deletionReason, messageUid, AdminComment }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { chatMessages } = useChatMessages(channelUid)

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { myPublicProfile } = useMyPublicProfile('NotificationsList')

    const message = chatMessages?.find((m) => m.uid === messageUid)
    const creatorUserDid = message?.creatorUserDid
    const deletedByRole = message?.deletedByRole

    const myPublicProfileDid = myPublicProfile.did

    let notificationMessage

    if(myPublicProfileDid === creatorUserDid) {
      notificationMessage = (
        <span>
          Your message in <ChannelName channelUid={channelUid}/> has been deleted by {deletedByRole} .
          Reason: {deletionReason}
          {AdminComment && <><br/>Comment: {AdminComment}</>}
          <br>----------</br>
          <br/><PublicProfileName did={creatorUserDid}/>
          <br/>Message: {message ? message.message : 'Message not found'}
        </span>
      )
    } else {
      notificationMessage = (
        <span>
          You deleted a message for user  <br/><PublicProfileName did={creatorUserDid}/>
          &nbsp;in <ChannelName channelUid={channelUid}/>
          &nbsp;as the {deletedByRole}.
          Reason: {deletionReason}
          {AdminComment && <><br/>Comment: {AdminComment}</>}
          <br>----------</br>
          <br/>Message: {message ? message.message : 'Message not found'}
        </span>
      )
    }

    return {
      icon: <Icon type="trash" />,
      // eslint-disable-next-line new-cap
      href: `/${ChannelName({ channelUid })}/chat/${channelUid}?message-${messageUid}`,
      message: notificationMessage,
      onClick: () => {
        navigateToMessage(messageUid)
      }
    }
  },

  chatMessageUndeleteRequest: ({ channelUid, messageUid}) => {

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { chatMessages } = useChatMessages(channelUid)

    const message = chatMessages?.find((m) => m.uid === messageUid)

    if (!message) {
      console.warn("Message not found in chatMessages for uid:", messageUid)
    }

    const creatorUserDid = message?.creatorUserDid

    return {
      icon: <Icon type="edit" />,
      // eslint-disable-next-line new-cap
      href: `/${ChannelName({ channelUid })}/chat/${channelUid}?message-${messageUid}`,
      message: (
        <span>
        User <PublicProfileName did={creatorUserDid}/> has requested to undo the deletion of
         their message in <ChannelName channelUid={channelUid} /> channel.
          <br />Modified Message: {message ? message.editedMessage : 'Message not found'}
          <br />Previous Message: {message ? message.message : 'Message not found'}
        </span>
      ),
      onClick: () => {
        navigateToMessage(messageUid)
      }
    }
  },

  chatMessageReinstated: ({ channelUid, messageUid }) => {

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { chatMessages } = useChatMessages(channelUid)
    const message = chatMessages?.find((m) => m.uid === messageUid)
    const deletedByRole = message?.deletedByRole


    return {
      icon: <Icon type="undo" />,

      // eslint-disable-next-line new-cap
      href: `/${ChannelName({ channelUid })}/chat/${channelUid}?message-${messageUid}`,
      message: (
        <span>
          Your message in <ChannelName channelUid={channelUid} /> has been reinstated by the {deletedByRole}.
          <br />Message: {message ? message.editedMessage : 'Message not found'}
        </span>
      ),
      onClick: () => {
        navigateToMessage(messageUid)
      }
    }
  },
  chatMessageUndeleteDenied: ({ channelUid, messageUid }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { chatMessages } = useChatMessages(channelUid)
    const message = chatMessages?.find((m) => m.uid === messageUid)
    const creatorUserDid = message?.creatorUserDid

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { myPublicProfile } = useMyPublicProfile('NotificationsList')

    const myPublicProfileDid = myPublicProfile.did

    let notificationMessage

    if (myPublicProfileDid === creatorUserDid) {
      notificationMessage = (
        <span>
          Your request to undelete a message in <ChannelName channelUid={channelUid} /> was denied.
          <br />Message: {message ? message.editedMessage : 'Message not found'}
        </span>
      )
    } else {
      notificationMessage = (
        <span>
          You denied a request to undelete a message in <ChannelName channelUid={channelUid} />.
          <br />User: <PublicProfileName did={creatorUserDid} />
          <br />Message: {message ? message.editedMessage : 'Message not found'}
        </span>
      )
    }

    return {
      icon: <Icon type="cancel" />,
      // eslint-disable-next-line new-cap
      href: `/${ChannelName({ channelUid })}/chat/${channelUid}?message-${messageUid}`,

      message: notificationMessage,
      onClick: () => navigateToMessage(messageUid)
    }
  },

  postDeletedByAdminToReposter: ({ organizationApikey, fromOrganizationApikey, feedPostUid, reason, comment }) => ({
    icon: <Icon type="trash"/>,
    href: `/${organizationApikey}/forum/${feedPostUid}`,
    message: <span>
      The post you re-posted to&nbsp;
      <OrganizationName {...{ organizationApikey }}/> &nbsp;
      was deleted by an admin of <OrganizationName {...{
        organizationApikey: fromOrganizationApikey,
      }} />&nbsp;
      {reason && <span> for {reason}</span>} <br/>
      {comment && <span>Admin comment: {comment}</span>}
    </span>,
  }),
  postDeletedByAdmin: ({ organizationApikey, feedPostUid, reason, comment }) => ({
    icon: <Icon type="trash"/>,
    href: `/${organizationApikey}/forum/${feedPostUid}`,
    message: <span>
      Your post was deleted by an admin of&nbsp;
      <OrganizationName {...{organizationApikey}}/>
      {reason && <span> for {reason}</span>} <br/>
      {comment && <span>Admin comment: {comment}</span>}
    </span>,
  }),
  productSale: ({
    organizationApikey,
    productId,
    productName,
    quantity,
    totalAmount,
    orderNumber,
  }) => ({
    icon: <Icon type="newPurchase" />,
    href: `/${organizationApikey}/marketplace/products/${productId}`,
    message: <span>
      🎉 Your product <strong>{productName}</strong> was purchased! <br/>
      Quantity: {Number(quantity)} • Total: ${(Number(totalAmount) / 100).toFixed(2)} <br/>
      Order: <strong>{orderNumber}</strong>
    </span>,
  }),

  hubSupportReceived: ({
    organizationApikey,
    supporterUsername,
    amountCents,
    isRecurring,
  }) => ({
    icon: <Icon type="heart" />,
    href: `/${organizationApikey}/admin/supporters`,
    message: <span>
      💚 <strong>{supporterUsername}</strong> started supporting your hub!
      <br/>
      Amount: ${(Number(amountCents) / 100).toFixed(2)}
      {isRecurring === 'true' ? ' • Monthly recurring' : ' • One-time'}
      <br/>
      <em>Thank you for building an amazing community!</em>
    </span>,
  }),

  orderConfirmed: ({ orderNumber, totalAmount, currency }) => ({
    icon: <Icon type="checkmark" />,
    href: `/orders/${orderNumber}`,
    message: <span>
      Your order <strong>#{orderNumber}</strong> has been confirmed!
      <br/>Total: {currency === 'USD' ? '$' : ''}{(Number(totalAmount) / 100).toFixed(2)}
    </span>,
  }),

  orderProcessing: ({ orderNumber }) => ({
    icon: <Icon type="package" />,
    href: `/orders/${orderNumber}`,
    message: <span>
      Your order <strong>#{orderNumber}</strong> is now being processed
    </span>,
  }),

  orderShipped: ({ orderNumber, trackingNumber, carrier }) => ({
    icon: <Icon type="truck" />,
    href: `/orders/${orderNumber}`,
    message: <span>
      Your order <strong>#{orderNumber}</strong> has shipped!
      {trackingNumber && <><br/>Tracking: <strong>{trackingNumber}</strong></>}
      {carrier && <><br/>Carrier: {carrier}</>}
    </span>,
  }),

  orderDelivered: ({ orderNumber }) => ({
    icon: <Icon type="checkmark" />,
    href: `/orders/${orderNumber}`,
    message: <span>
      Your order <strong>#{orderNumber}</strong> has been delivered! 🎉
    </span>,
  }),

  orderStatusUpdate: ({ orderNumber, newStatus, updateReason, isReversion }) => ({
    icon: <Icon type="info" />,
    href: `/orders/${orderNumber}`,
    message: <span>
      Update on your order <strong>#{orderNumber}</strong>: Status changed to {newStatus}
      {isReversion && updateReason && <><br/>Reason: {updateReason}</>}
    </span>,
  }),

  orderCancelled: ({ orderNumber, cancellationReason }) => ({
    icon: <Icon type="cancel" />,
    href: `/orders/${orderNumber}`,
    message: <span>
      Your order <strong>#{orderNumber}</strong> has been cancelled
      {cancellationReason && <><br/>Reason: {cancellationReason}</>}
    </span>,
  }),

  customerContactedSeller: ({ orderNumber, customerName, messagePreview }) => ({
    icon: <Icon type="chat" />,
    href: `/seller/customer-service/${orderNumber}`,
    message: <span>
      New message from customer <strong>{customerName}</strong> about order <strong>#{orderNumber}</strong>
      {messagePreview && <><br/>"{messagePreview}"</>}
    </span>,
  }),

  sellerRepliedToCustomer: ({ orderNumber, sellerName, messagePreview }) => ({
    icon: <Icon type="chat" />,
    href: `/orders/customer-service/${orderNumber}`,
    message: <span>
      <strong>{sellerName}</strong> replied to your message about order <strong>#{orderNumber}</strong>
      {messagePreview && <><br/>"{messagePreview}"</>}
    </span>,
  }),

  subOrgAdminInherited: ({ organizationApikey, parentOrganizationApikey }) => ({
    icon: <OrganizationIcon {...{organizationApikey}} />,
    href: `/${organizationApikey}/members`,
    message: <span>
      You have been added as an admin to <OrganizationName {...{organizationApikey}}/>,
      a hub of <OrganizationName organizationApikey={parentOrganizationApikey}/>
    </span>,
  }),
}

function NotificationWithErrorCatching({ notification, myPublicProfile, onNotificationClick, ...props }){
  const [error, onDismiss] = useErrorBoundary()
  if (error) {
    console.error(error)
    return <ErrorMessage {...{error, onDismiss}} />
  }
  if (notification.type in TYPES){
    const {icon, href, message, onClick} = TYPES[notification.type]({myPublicProfile, ...notification})
    return <Notification {...{...props, notification, icon, href, message, onClick, onNotificationClick}}/>
  }
  //! CANCEL unknown notification
  // return <Notification {...{
  //   notification,
  //   message: `ERROR: unknown notification type=${notification.type}`,
  //   onNotificationClick,
  // }}/>
}

function Notification({
  notification: { type, uid, seenAt, createdAt },
  icon, href, message, onClick: customOnClick,
  onNotificationClick,
}) {
  const unread = !seenAt
  const ref = useRef()
  const { takeAction } = useAppState(undefined, 'NotificationsList')

  useOnScrolledIntoView(
    ref,
    () => { if (unread) takeAction('notifications.markAsRead', uid) },
    [uid, unread]
  )

  const handleClick = (event) => {
    // Call custom onClick if it exists (for special notification types)
    if (customOnClick) {
      customOnClick(event)
    }
    // Always call the onNotificationClick callback to close the modal
    if (onNotificationClick) {
      onNotificationClick(event)
    }
  }

  return <Link {...{
    className: classNames('NotificationsList-Notification', { unread, [type]: 1 }),
    ref, href,
    onClick: handleClick,
  }}>
    <div>{icon}</div>
    <div>
      <div>{message}</div>
      <TimeAgo time={createdAt} />
    </div>
  </Link>
}

function OrganizationName({ organizationApikey }){
  const { organization } = useOrganization(organizationApikey, 'NotificationsList')
  return <span>{(organization && organization.name) || organizationApikey}</span>
}

function PublicProfileName({ did }){
  const { publicProfile = {} } = usePublicProfile(did, 'NotificationsList')
  return <span>{publicProfileToDisplayName(publicProfile)}</span>
}

function OrganizationIcon({ organizationApikey }){
  const { organization } = useOrganization(organizationApikey, 'NotificationsList')
  return <_OrganizationIcon {...{organization}}/>
}

export function ChannelName({ channelUid }) {
  const { chatSynopsis } = useChatSynopsis("NotificationsList")
  const channelName = useMemo(() => {
    const uid =
      typeof channelUid === "object" ? channelUid.channelUid : channelUid
    const channel = chatSynopsis.find((c) => c.uid === uid)
    return channel ? channel.organizationApikey : "Unknown Channel"
  }, [chatSynopsis, channelUid])

  return channelName
}

export function navigateToMessage(messageUid) {
  const element = document.getElementById(`message-${messageUid}`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', inline: "center", block: "center"})
    element.classList.add('highlighted-message')
    setTimeout(() => {
      element.classList.remove('highlighted-message')
    }, 5000)
  } else {
    console.log("Element not found for id:", `message-${messageUid}`)
  }
}
